import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchAllTasks, fetchThemes, createTask, updateTask, batchDeleteTasks, importTasks, exportTasks } from '../api/projects';
import { notifyFounders } from '../api/transcripts';
import { fetchPeople } from '../api/people';
import type { Task, TaskStatus, TaskPriority, Theme } from '../types/project';
import type { Person } from '../types/people';
import { TASK_STATUSES, STATUS_LABEL, STATUS_BADGE } from '../utils/taskStatus';
import { projectLabel } from '../utils/task';
import { formatDateDDMMYYYY } from '../utils/date';
import AvatarStack from '../components/AvatarStack';
import TaskAssigneesPicker from '../components/TaskAssigneesPicker';

const PRIORITY_COLOR: Record<string, string> = {
  LOW: 'var(--color-text-muted)',
  MEDIUM: 'var(--color-blue-dark)',
  HIGH: 'var(--color-coral)',
};

const COL_BORDER = 'rgba(0,0,0,0.08)';

const PRIORITY_RANK: Record<TaskPriority, number> = { HIGH: 0, MEDIUM: 1, LOW: 2 };
const STATUS_RANK: Record<TaskStatus, number> = TASK_STATUSES.reduce((acc, s, idx) => {
  acc[s] = idx;
  return acc;
}, {} as Record<TaskStatus, number>);

const DRAG_DATA_KEY = 'application/x-jambo-task-id';

type SortKey = 'status' | 'priority' | 'due_date';
type SortDir = 'asc' | 'desc';

interface NewTaskForm {
  title: string;
  theme_id: string;
  sub_theme_id: string;
  status: TaskStatus;
  priority: TaskPriority;
  assignee_id: string;
  due_date: string;
}

const EMPTY_FORM: NewTaskForm = {
  title: '',
  theme_id: '',
  sub_theme_id: '',
  status: 'TO_DO',
  priority: 'MEDIUM',
  assignee_id: '',
  due_date: '',
};

interface MatrixGroup {
  key: string;
  label: string;
  byStatus: Record<TaskStatus, Task[]>;
}

function groupTasksMatrix(tasks: Task[], statuses: TaskStatus[]): MatrixGroup[] {
  const groups = new Map<string, MatrixGroup>();
  for (const task of tasks) {
    const label = projectLabel(task) || 'No theme';
    let group = groups.get(label);
    if (!group) {
      const byStatus = {} as Record<TaskStatus, Task[]>;
      for (const s of statuses) byStatus[s] = [];
      group = { key: label, label, byStatus };
      groups.set(label, group);
    }
    group.byStatus[task.status].push(task);
  }
  return Array.from(groups.values()).sort((a, b) => a.label.localeCompare(b.label));
}

function sortTasks(tasks: Task[], sort: { key: SortKey; dir: SortDir }): Task[] {
  const dirMult = sort.dir === 'asc' ? 1 : -1;
  const sorted = [...tasks];
  sorted.sort((a, b) => {
    if (sort.key === 'status') return (STATUS_RANK[a.status] - STATUS_RANK[b.status]) * dirMult;
    if (sort.key === 'priority') {
      return (PRIORITY_RANK[a.priority ?? 'MEDIUM'] - PRIORITY_RANK[b.priority ?? 'MEDIUM']) * dirMult;
    }
    const aNull = !a.due_date;
    const bNull = !b.due_date;
    if (aNull && bNull) return 0;
    if (aNull) return 1;
    if (bNull) return -1;
    if (a.due_date! < b.due_date!) return -1 * dirMult;
    if (a.due_date! > b.due_date!) return 1 * dirMult;
    return 0;
  });
  return sorted;
}

export default function MasterTasksPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const viewMode: 'board' | 'list' = location.pathname === '/tasks/board' ? 'board' : 'list';

  const [tasks, setTasks] = useState<Task[]>([]);
  const [themes, setThemes] = useState<Theme[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [assigneeFilter, setAssigneeFilter] = useState<Set<string>>(new Set());
  const [themeFilter, setThemeFilter] = useState<Set<string>>(new Set());
  const [subThemeFilter, setSubThemeFilter] = useState<Set<string>>(new Set());
  const [openFilter, setOpenFilter] = useState<'assignee' | 'theme' | 'subtheme' | null>(null);
  const [dueByFilter, setDueByFilter] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: 'due_date', dir: 'asc' });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState<NewTaskForm>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOverCell, setDragOverCell] = useState<{ groupKey: string; status: TaskStatus } | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set());
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [importMsg, setImportMsg] = useState('');
  const [importError, setImportError] = useState(false);
  const [notifyMsg, setNotifyMsg] = useState('');
  const [notifyError, setNotifyError] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const founders = useMemo(() => people.filter((p) => p.person_type === 'founder'), [people]);
  const rootThemes = useMemo(
    () => themes.filter((t) => !t.parent_theme_id).sort((a, b) => a.order_index - b.order_index || a.name.localeCompare(b.name)),
    [themes]
  );
  const subThemesForFilter = useMemo(() => {
    if (themeFilter.size === 0) return themes.filter((t) => t.parent_theme_id);
    return themes.filter((t) => t.parent_theme_id && themeFilter.has(t.parent_theme_id));
  }, [themes, themeFilter]);
  const subThemesForForm = useMemo(
    () => (form.theme_id ? themes.filter((t) => t.parent_theme_id === form.theme_id) : []),
    [themes, form.theme_id]
  );

  useEffect(() => {
    Promise.all([fetchThemes(), fetchPeople()])
      .then(([t, p]) => {
        setThemes(t);
        setPeople(p);
      })
      .catch((e: Error) => setError(e.message));
  }, []);

  const reloadTasks = useCallback(() => {
    fetchAllTasks()
      .then((ts) => {
        setTasks(ts);
        setSelectedTaskIds(new Set());
        setError(null);
      })
      .catch((e: Error) => setError(e.message));
  }, []);

  useEffect(() => {
    reloadTasks();
  }, [reloadTasks]);

  useEffect(() => {
    if (themeFilter.size === 0) return;
    setSubThemeFilter((prev) => {
      const next = new Set(prev);
      for (const subId of prev) {
        const sub = themes.find((t) => t.id === subId);
        if (sub && sub.parent_theme_id && !themeFilter.has(sub.parent_theme_id)) next.delete(subId);
      }
      return next.size === prev.size ? prev : next;
    });
  }, [themeFilter, themes]);

  useEffect(() => {
    if (!openFilter) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target || !target.closest('[data-filter-dropdown]')) setOpenFilter(null);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [openFilter]);

  const filteredTasks = useMemo(() => {
    let result = tasks;
    if (assigneeFilter.size > 0) {
      result = result.filter((t) => {
        const ids = t.assignee_ids?.length ? t.assignee_ids : t.assignee ? [t.assignee] : [];
        return ids.some((id) => assigneeFilter.has(id));
      });
    }
    if (themeFilter.size > 0) {
      result = result.filter((t) => {
        if (themeFilter.has(t.theme_id)) return true;
        const theme = themes.find((th) => th.id === t.theme_id);
        return !!(theme?.parent_theme_id && themeFilter.has(theme.parent_theme_id));
      });
    }
    if (subThemeFilter.size > 0) {
      result = result.filter((t) => subThemeFilter.has(t.theme_id));
    }
    if (!showCompleted) {
      result = result.filter((t) => t.status !== 'COMPLETED' && t.status !== 'CANCELLED');
    }
    if (dueByFilter) {
      result = result.filter((t) => !t.due_date || t.due_date <= dueByFilter);
    }
    return result;
  }, [tasks, themes, assigneeFilter, themeFilter, subThemeFilter, showCompleted, dueByFilter]);

  const hasActiveFilter =
    assigneeFilter.size > 0 ||
    themeFilter.size > 0 ||
    subThemeFilter.size > 0 ||
    !!dueByFilter ||
    showCompleted;

  const clearAllFilters = useCallback(() => {
    setAssigneeFilter(new Set());
    setThemeFilter(new Set());
    setSubThemeFilter(new Set());
    setDueByFilter('');
    setShowCompleted(false);
  }, []);

  const toggleGroup = useCallback((key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const collapseAll = useCallback((keys: string[]) => {
    setCollapsedGroups(new Set(keys));
  }, []);

  const expandAll = useCallback(() => {
    setCollapsedGroups(new Set());
  }, []);

  const handleStatusChange = useCallback(
    (taskId: string, newStatus: TaskStatus) => {
      setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, status: newStatus } : t)));
      updateTask(taskId, { status: newStatus }).catch((e: Error) => {
        setError(e.message);
        reloadTasks();
      });
    },
    [reloadTasks]
  );

  const handleAssigneeChange = useCallback(
    (taskId: string, personId: string) => {
      const nextAssignee = personId || null;
      setTasks((prev) =>
        prev.map((t) =>
          t.id === taskId
            ? { ...t, assignee: nextAssignee, assignee_ids: personId ? [personId] : [] }
            : t
        )
      );
      updateTask(taskId, { assignee: nextAssignee })
        .then(() => reloadTasks())
        .catch((e: Error) => {
          setError(e.message);
          reloadTasks();
        });
    },
    [reloadTasks]
  );

  const handleMultiAssigneeChange = useCallback(
    (taskId: string, personIds: string[]) => {
      setTasks((prev) =>
        prev.map((t) =>
          t.id === taskId
            ? { ...t, assignee: personIds[0] ?? null, assignee_ids: personIds }
            : t
        )
      );
      updateTask(taskId, { assignee_ids: personIds })
        .then(() => reloadTasks())
        .catch((e: Error) => {
          setError(e.message);
          reloadTasks();
        });
    },
    [reloadTasks]
  );

  const handleViewToggle = useCallback(
    (next: 'board' | 'list') => {
      navigate(next === 'board' ? '/tasks/board' : '/tasks/list');
    },
    [navigate]
  );

  const handleSort = useCallback((key: SortKey) => {
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  }, []);

  const toggleTaskOne = useCallback((id: string) => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleTaskAll = useCallback(() => {
    setSelectedTaskIds((prev) => {
      const ids = filteredTasks.map((t) => t.id);
      const allSelected = ids.length > 0 && ids.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  }, [filteredTasks]);

  const applyDeleteTasks = useCallback(async () => {
    if (selectedTaskIds.size === 0) return;
    setDeleting(true);
    try {
      await batchDeleteTasks(Array.from(selectedTaskIds));
      setSelectedTaskIds(new Set());
      setDeleteConfirmOpen(false);
      reloadTasks();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete tasks');
    } finally {
      setDeleting(false);
    }
  }, [selectedTaskIds, reloadTasks]);

  const handleFileSelected = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImportMsg('Importing…');
    setImportError(false);
    try {
      const r = await importTasks(file);
      const base = `${r.imported} tasks imported`;
      if (r.errors.length) {
        setImportError(true);
        setImportMsg(`${base}; ${r.errors.length} error(s): ${r.errors.map((x) => `row ${x.row}: ${x.message}`).join('; ')}`);
      } else {
        setImportError(false);
        setImportMsg(base);
      }
      fetchThemes().then((t) => setThemes(t)).catch(() => {});
      reloadTasks();
    } catch (err) {
      setImportError(true);
      setImportMsg(err instanceof Error ? err.message : 'Import failed');
    }
  }, [reloadTasks]);

  const handleNotifyFounders = useCallback(async () => {
    setNotifying(true);
    setNotifyMsg('');
    setNotifyError(false);
    try {
      const result = await notifyFounders();
      const msg =
        result.sent === 0
          ? 'No founders with open tasks to notify'
          : `Emailed ${result.sent} founder(s)${result.skipped > 0 ? `, ${result.skipped} failed` : ''}`;
      setNotifyMsg(msg);
      setNotifyError(false);
    } catch {
      setNotifyMsg('Failed to send notifications');
      setNotifyError(true);
    } finally {
      setNotifying(false);
    }
  }, []);

  const openDrawer = useCallback(() => {
    const theme_id = themeFilter.size === 1 ? [...themeFilter][0] : '';
    const sub_theme_id = subThemeFilter.size === 1 ? [...subThemeFilter][0] : '';
    setForm({ ...EMPTY_FORM, theme_id, sub_theme_id });
    setDrawerOpen(true);
  }, [themeFilter, subThemeFilter]);

  const handleCreateTask = useCallback(() => {
    if (!form.title.trim() || !form.theme_id) return;
    setSubmitting(true);
    const themeId = form.sub_theme_id || form.theme_id;
    createTask({
      theme_id: themeId,
      title: form.title.trim(),
      status: form.status,
      priority: form.priority,
      assignee_ids: form.assignee_id ? [form.assignee_id] : undefined,
      due_date: form.due_date || undefined,
    })
      .then(() => {
        setDrawerOpen(false);
        setForm(EMPTY_FORM);
        reloadTasks();
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setSubmitting(false));
  }, [form, reloadTasks]);

  // Drag handlers (board)
  const onDragStart = useCallback((e: React.DragEvent, taskId: string) => {
    e.dataTransfer.setData(DRAG_DATA_KEY, taskId);
    e.dataTransfer.effectAllowed = 'move';
    setDraggingId(taskId);
  }, []);
  const onDragEnd = useCallback(() => {
    setDraggingId(null);
    setDragOverCell(null);
  }, []);
  const onDragOver = useCallback((e: React.DragEvent, groupKey: string, status: TaskStatus) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCell({ groupKey, status });
  }, []);
  const onDragLeave = useCallback(() => setDragOverCell(null), []);
  const onDrop = useCallback(
    (e: React.DragEvent, newStatus: TaskStatus) => {
      e.preventDefault();
      setDragOverCell(null);
      setDraggingId(null);
      const taskId = e.dataTransfer.getData(DRAG_DATA_KEY);
      if (!taskId) return;
      const task = tasks.find((t) => t.id === taskId);
      if (task && task.status !== newStatus) handleStatusChange(taskId, newStatus);
    },
    [tasks, handleStatusChange]
  );

  return (
    <>
      <p
        style={{
          fontSize: '13px',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--color-blue-dark)',
          fontWeight: 600,
          marginBottom: '8px',
        }}
      >
        Tasks
      </p>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '16px',
          marginBottom: '24px',
        }}
      >
        <h1 style={{ fontSize: '36px', fontWeight: 700, margin: 0 }}>
          {viewMode === 'board' ? 'Board' : 'List'}
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={() => handleViewToggle('list')}
              style={{
                padding: '8px 16px',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius)',
                background: viewMode === 'list' ? 'var(--color-coral)' : 'white',
                color: viewMode === 'list' ? 'white' : 'var(--color-text-primary)',
                fontWeight: 500,
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              List
            </button>
            <button
              type="button"
              onClick={() => handleViewToggle('board')}
              style={{
                padding: '8px 16px',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius)',
                background: viewMode === 'board' ? 'var(--color-coral)' : 'white',
                color: viewMode === 'board' ? 'white' : 'var(--color-text-primary)',
                fontWeight: 500,
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Board
            </button>
          </div>
          <button type="button" onClick={exportTasks} className="btn btn-secondary">
            Export CSV
          </button>
          <button type="button" onClick={() => fileInputRef.current?.click()} className="btn btn-secondary">
            Import CSV
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={handleFileSelected}
          />
          <button
            type="button"
            onClick={handleNotifyFounders}
            className="btn btn-secondary"
            disabled={notifying}
          >
            {notifying ? 'Sending…' : 'Notify founders'}
          </button>
          <button type="button" onClick={openDrawer} className="btn btn-primary">
            Add task
          </button>
        </div>
      </div>

      <div
        data-filter-dropdown
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap',
          marginBottom: '20px',
        }}
      >
        <MultiSelectDropdown
          label="Assignee"
          options={founders.map((p) => ({ value: p.id, label: p.name }))}
          selected={assigneeFilter}
          onChange={setAssigneeFilter}
          isOpen={openFilter === 'assignee'}
          onToggleOpen={() => setOpenFilter(openFilter === 'assignee' ? null : 'assignee')}
        />
        <MultiSelectDropdown
          label="Theme"
          options={rootThemes.map((t) => ({ value: t.id, label: t.name }))}
          selected={themeFilter}
          onChange={setThemeFilter}
          isOpen={openFilter === 'theme'}
          onToggleOpen={() => setOpenFilter(openFilter === 'theme' ? null : 'theme')}
        />
        <MultiSelectDropdown
          label="Sub-theme"
          options={subThemesForFilter.map((t) => ({ value: t.id, label: t.name }))}
          selected={subThemeFilter}
          onChange={setSubThemeFilter}
          isOpen={openFilter === 'subtheme'}
          onToggleOpen={() => setOpenFilter(openFilter === 'subtheme' ? null : 'subtheme')}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>Due by:</span>
          <input
            type="date"
            value={dueByFilter}
            onChange={(e) => setDueByFilter(e.target.value)}
            style={{
              padding: '6px 12px',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius)',
              background: dueByFilter ? 'var(--color-warm-bg)' : 'white',
              fontSize: '13px',
              fontWeight: dueByFilter ? 600 : 400,
              cursor: 'pointer',
            }}
          />
          {dueByFilter && (
            <button
              type="button"
              onClick={() => setDueByFilter('')}
              aria-label="Clear due by filter"
              style={{
                padding: '4px 8px',
                background: 'transparent',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius)',
                cursor: 'pointer',
                fontSize: '14px',
                lineHeight: 1,
                color: 'var(--color-text-secondary)',
              }}
            >
              ×
            </button>
          )}
        </div>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '13px',
            color: 'var(--color-text-secondary)',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={showCompleted}
            onChange={(e) => setShowCompleted(e.target.checked)}
          />
          Show completed
        </label>
        {hasActiveFilter && (
          <button
            type="button"
            onClick={clearAllFilters}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--color-blue-dark)',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            Clear all filters
          </button>
        )}
      </div>

      {notifyMsg && (
        <div
          style={{
            marginBottom: '16px',
            padding: '10px 14px',
            border: `1px solid ${notifyError ? 'var(--color-coral)' : 'var(--color-border)'}`,
            background: 'white',
            borderRadius: 'var(--radius)',
            fontSize: '13px',
            color: notifyError ? 'var(--color-coral)' : 'var(--color-text-muted)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <span>{notifyMsg}</span>
          <button
            type="button"
            onClick={() => setNotifyMsg('')}
            style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: '16px' }}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {importMsg && (
        <div
          style={{
            marginBottom: '16px',
            padding: '10px 14px',
            border: `1px solid ${importError ? 'var(--color-coral)' : 'var(--color-border)'}`,
            background: 'white',
            borderRadius: 'var(--radius)',
            fontSize: '13px',
            color: importError ? 'var(--color-coral)' : 'var(--color-text-muted)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <span>{importMsg}</span>
          <button
            type="button"
            onClick={() => setImportMsg('')}
            style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: '16px' }}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {error && (
        <div style={{ color: 'var(--color-coral)', marginBottom: '16px', fontSize: '14px' }}>{error}</div>
      )}

      {viewMode === 'list' && selectedTaskIds.size > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
          padding: '10px 14px', marginBottom: '16px',
          border: '1px solid var(--color-border)', borderRadius: 'var(--radius)',
          background: 'var(--color-warm-bg)', fontSize: '14px',
        }}>
          <span style={{ fontWeight: 600 }}>{selectedTaskIds.size} tasks selected</span>
          <button type="button" onClick={() => setDeleteConfirmOpen(true)} className="btn btn-danger btn-sm">
            Delete selected
          </button>
          <button
            type="button"
            onClick={() => setSelectedTaskIds(new Set())}
            style={{ background: 'transparent', border: 'none', color: 'var(--color-blue-dark)', cursor: 'pointer', textDecoration: 'underline', fontSize: '13px' }}
          >
            Clear selection
          </button>
        </div>
      )}

      {viewMode === 'board' ? (
        <MatrixBoardView
          statuses={TASK_STATUSES}
          tasks={filteredTasks}
          founders={founders}
          showCompleted={showCompleted}
          collapsedGroups={collapsedGroups}
          onToggleGroup={toggleGroup}
          onCollapseAll={collapseAll}
          onExpandAll={expandAll}
          dragOverCell={dragOverCell}
          draggingId={draggingId}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onCardClick={(t) => navigate(`/projects/task/${t.id}`)}
          onAssigneeChange={handleAssigneeChange}
        />
      ) : (
        <ListView
          tasks={sortTasks(filteredTasks, sort)}
          founders={founders}
          sort={sort}
          onSort={handleSort}
          onRowClick={(t) => navigate(`/projects/task/${t.id}`)}
          onAssigneeChange={handleMultiAssigneeChange}
          selectedIds={selectedTaskIds}
          onToggleOne={toggleTaskOne}
          onToggleAll={toggleTaskAll}
        />
      )}

      {drawerOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.3)',
            zIndex: 100,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
          onClick={() => setDrawerOpen(false)}
          role="presentation"
        >
          <div
            style={{
              width: '520px',
              maxHeight: '100vh',
              background: 'white',
              boxShadow: '-4px 0 24px rgba(0,0,0,0.1)',
              padding: '24px',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 16px' }}>New task</h2>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>
              Title *
            </label>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius)',
                marginBottom: '12px',
              }}
            />
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>
              Theme *
            </label>
            <select
              value={form.theme_id}
              onChange={(e) => setForm({ ...form, theme_id: e.target.value, sub_theme_id: '' })}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius)',
                marginBottom: '12px',
              }}
            >
              <option value="">— select theme —</option>
              {rootThemes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>
              Sub-theme
            </label>
            <select
              value={form.sub_theme_id}
              onChange={(e) => setForm({ ...form, sub_theme_id: e.target.value })}
              disabled={!form.theme_id || subThemesForForm.length === 0}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius)',
                marginBottom: '12px',
                background: !form.theme_id || subThemesForForm.length === 0 ? 'var(--color-border)' : 'white',
              }}
            >
              <option value="">— none —</option>
              {subThemesForForm.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>
              Status
            </label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as TaskStatus })}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius)',
                marginBottom: '12px',
              }}
            >
              {TASK_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>
              Priority
            </label>
            <select
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value as TaskPriority })}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius)',
                marginBottom: '12px',
              }}
            >
              <option value="LOW">Low</option>
              <option value="MEDIUM">Medium</option>
              <option value="HIGH">High</option>
            </select>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>
              Assignee
            </label>
            <select
              value={form.assignee_id}
              onChange={(e) => setForm({ ...form, assignee_id: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius)',
                marginBottom: '12px',
              }}
            >
              <option value="">— unassigned —</option>
              {founders.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>
              Due date
            </label>
            <input
              type="date"
              value={form.due_date}
              onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius)',
                marginBottom: '16px',
              }}
            />
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={handleCreateTask}
                disabled={submitting || !form.title.trim() || !form.theme_id}
                className="btn btn-primary"
              >
                Create
              </button>
              <button type="button" onClick={() => setDrawerOpen(false)} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirmOpen && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => !deleting && setDeleteConfirmOpen(false)}
          role="presentation"
        >
          <div
            style={{ background: 'white', padding: '24px', borderRadius: 'var(--radius)', minWidth: '400px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px' }}>Permanently delete {selectedTaskIds.size} tasks? This cannot be undone.</h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" disabled={deleting} onClick={applyDeleteTasks} className="btn btn-danger">
                {deleting ? 'Deleting…' : 'Confirm'}
              </button>
              <button type="button" disabled={deleting} onClick={() => setDeleteConfirmOpen(false)} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

interface MultiSelectProps {
  label: string;
  options: { value: string; label: string }[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  isOpen: boolean;
  onToggleOpen: () => void;
}

function MultiSelectDropdown({ label, options, selected, onChange, isOpen, onToggleOpen }: MultiSelectProps) {
  const count = selected.size;
  const display = count === 0 ? label : `${label} (${count})`;
  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={onToggleOpen}
        style={{
          padding: '6px 12px',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius)',
          background: count > 0 ? 'var(--color-warm-bg)' : 'white',
          fontSize: '13px',
          fontWeight: count > 0 ? 600 : 400,
          cursor: 'pointer',
        }}
      >
        {display} ▾
      </button>
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            zIndex: 50,
            background: 'white',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius)',
            padding: '8px',
            minWidth: '180px',
            maxHeight: '260px',
            overflowY: 'auto',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          }}
        >
          {options.length === 0 && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', padding: '4px' }}>No options</div>
          )}
          {options.map((opt) => (
            <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px', fontSize: '13px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={selected.has(opt.value)}
                onChange={() => {
                  const next = new Set(selected);
                  if (next.has(opt.value)) next.delete(opt.value); else next.add(opt.value);
                  onChange(next);
                }}
              />
              {opt.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

interface MatrixBoardViewProps {
  statuses: TaskStatus[];
  tasks: Task[];
  founders: Person[];
  showCompleted: boolean;
  collapsedGroups: Set<string>;
  onToggleGroup: (key: string) => void;
  onCollapseAll: (keys: string[]) => void;
  onExpandAll: () => void;
  dragOverCell: { groupKey: string; status: TaskStatus } | null;
  draggingId: string | null;
  onDragStart: (e: React.DragEvent, taskId: string) => void;
  onDragEnd: () => void;
  onDragOver: (e: React.DragEvent, groupKey: string, status: TaskStatus) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent, status: TaskStatus) => void;
  onCardClick: (task: Task) => void;
  onAssigneeChange: (taskId: string, personId: string) => void;
}

const GRID_TEMPLATE_COLUMNS = '240px repeat(5, minmax(180px, 1fr))';
const GRID_MIN_WIDTH = 1140;

function MatrixBoardView({
  statuses,
  tasks,
  founders,
  showCompleted,
  collapsedGroups,
  onToggleGroup,
  onCollapseAll,
  onExpandAll,
  dragOverCell,
  draggingId,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  onCardClick,
  onAssigneeChange,
}: MatrixBoardViewProps) {
  const groups = useMemo(() => groupTasksMatrix(tasks, statuses), [tasks, statuses]);

  const totalByStatus = useMemo(() => {
    const totals = {} as Record<TaskStatus, number>;
    for (const s of statuses) totals[s] = 0;
    for (const t of tasks) totals[t.status] = (totals[t.status] ?? 0) + 1;
    return totals;
  }, [tasks, statuses]);

  const allKeys = useMemo(() => groups.map((g) => g.key), [groups]);
  const allCollapsed = allKeys.length > 0 && allKeys.every((k) => collapsedGroups.has(k));
  const noneCollapsed = collapsedGroups.size === 0;

  const stickyCellBase: React.CSSProperties = {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    background: 'white',
    borderBottom: `1px solid ${COL_BORDER}`,
    padding: '12px',
    display: 'flex',
    alignItems: 'center',
  };

  return (
    <div style={{ overflowX: 'auto', paddingBottom: '16px' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
          columnGap: 0,
          minWidth: `${GRID_MIN_WIDTH}px`,
          background: 'white',
          border: `1px solid ${COL_BORDER}`,
          borderRadius: 'var(--radius)',
          overflow: 'hidden',
        }}
      >
        <div style={{ ...stickyCellBase, gap: '8px', justifyContent: 'flex-start' }}>
          <button
            type="button"
            onClick={() => onCollapseAll(allKeys)}
            disabled={allCollapsed || allKeys.length === 0}
            style={{
              background: 'transparent',
              border: 'none',
              color: allCollapsed || allKeys.length === 0 ? 'var(--color-text-muted)' : 'var(--color-blue-dark)',
              cursor: allCollapsed || allKeys.length === 0 ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              padding: 0,
              textDecoration: 'underline',
            }}
          >
            Collapse all
          </button>
          <span style={{ color: 'var(--color-text-muted)', fontSize: '12px' }}>·</span>
          <button
            type="button"
            onClick={onExpandAll}
            disabled={noneCollapsed}
            style={{
              background: 'transparent',
              border: 'none',
              color: noneCollapsed ? 'var(--color-text-muted)' : 'var(--color-blue-dark)',
              cursor: noneCollapsed ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              padding: 0,
              textDecoration: 'underline',
            }}
          >
            Expand all
          </button>
        </div>
        {statuses.map((s) => {
          const dimmed = !showCompleted && (s === 'COMPLETED' || s === 'CANCELLED');
          return (
            <div
              key={s}
              style={{
                ...stickyCellBase,
                justifyContent: 'space-between',
                borderLeft: `1px solid ${COL_BORDER}`,
                opacity: dimmed ? 0.5 : 1,
              }}
            >
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: 'var(--color-text-secondary)',
                }}
              >
                {STATUS_LABEL[s]}
              </span>
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  background: STATUS_BADGE[s].bg,
                  color: STATUS_BADGE[s].color,
                  padding: '2px 8px',
                  borderRadius: '999px',
                  minWidth: '20px',
                  textAlign: 'center',
                }}
              >
                {totalByStatus[s]}
              </span>
            </div>
          );
        })}

        {groups.length === 0 && (
          <div
            style={{
              gridColumn: '1 / -1',
              padding: '32px 12px',
              color: 'var(--color-text-secondary)',
              textAlign: 'center',
            }}
          >
            No tasks.
          </div>
        )}

        {groups.map((group) => {
          const collapsed = collapsedGroups.has(group.key);
          return (
            <React.Fragment key={group.key}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => onToggleGroup(group.key)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onToggleGroup(group.key);
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px',
                  cursor: 'pointer',
                  userSelect: 'none',
                  minWidth: 0,
                  background: 'white',
                  borderBottom: collapsed ? `1px solid ${COL_BORDER}` : 'none',
                }}
              >
                <span
                  aria-hidden
                  style={{
                    fontSize: '10px',
                    color: 'var(--color-text-muted)',
                    width: '12px',
                    display: 'inline-block',
                    transform: collapsed ? 'none' : 'rotate(0deg)',
                  }}
                >
                  {collapsed ? '▶' : '▼'}
                </span>
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: '13px',
                    color: 'var(--color-text-primary)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  title={group.label}
                >
                  {group.label}
                </span>
              </div>
              {statuses.map((s) => {
                const count = group.byStatus[s].length;
                const dimmed = !showCompleted && (s === 'COMPLETED' || s === 'CANCELLED');
                if (!collapsed) {
                  return (
                    <div
                      key={s}
                      style={{
                        padding: '12px',
                        background: 'white',
                        borderLeft: `1px solid ${COL_BORDER}`,
                      }}
                    />
                  );
                }
                return (
                  <div
                    key={s}
                    style={{
                      padding: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      opacity: dimmed ? 0.5 : 1,
                      background: 'white',
                      borderLeft: `1px solid ${COL_BORDER}`,
                      borderBottom: `1px solid ${COL_BORDER}`,
                    }}
                  >
                    {count > 0 && (
                      <span
                        style={{
                          fontSize: '11px',
                          fontWeight: 600,
                          background: 'rgba(0,0,0,0.06)',
                          color: 'var(--color-text-secondary)',
                          padding: '2px 8px',
                          borderRadius: '999px',
                          minWidth: '20px',
                          textAlign: 'center',
                        }}
                      >
                        {count}
                      </span>
                    )}
                  </div>
                );
              })}

              {!collapsed && (
                <React.Fragment>
                  <div style={{ background: 'white', borderBottom: `1px solid ${COL_BORDER}` }} />
                  {statuses.map((s) => {
                    const dimmed = !showCompleted && (s === 'COMPLETED' || s === 'CANCELLED');
                    const isDragOver =
                      dragOverCell?.groupKey === group.key && dragOverCell?.status === s;
                    const cellTasks = group.byStatus[s];
                    const isEmpty = cellTasks.length === 0;
                    return (
                      <div
                        key={s}
                        onDragOver={(e) => onDragOver(e, group.key, s)}
                        onDragLeave={onDragLeave}
                        onDrop={(e) => onDrop(e, s)}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                          padding: '12px',
                          minHeight: '64px',
                          background: isDragOver ? 'rgba(30, 58, 95, 0.06)' : 'white',
                          borderLeft: isDragOver
                            ? '1px dashed var(--color-blue-dark)'
                            : `1px solid ${COL_BORDER}`,
                          borderRight: isDragOver ? '1px dashed var(--color-blue-dark)' : undefined,
                          borderTop: isDragOver ? '1px dashed var(--color-blue-dark)' : undefined,
                          borderBottom: isDragOver
                            ? '1px dashed var(--color-blue-dark)'
                            : `1px solid ${COL_BORDER}`,
                          opacity: dimmed ? 0.5 : 1,
                          transition: 'background 120ms, border-color 120ms',
                        }}
                      >
                        {isEmpty && !isDragOver && (
                          <div
                            style={{
                              flex: 1,
                              minHeight: '60px',
                              border: '1px dashed rgba(0,0,0,0.12)',
                              borderRadius: '8px',
                            }}
                          />
                        )}
                        {cellTasks.map((task) => (
                          <TaskCard
                            key={task.id}
                            task={task}
                            founders={founders}
                            dragging={draggingId === task.id}
                            onDragStart={onDragStart}
                            onDragEnd={onDragEnd}
                            onClick={onCardClick}
                            onAssigneeChange={onAssigneeChange}
                          />
                        ))}
                      </div>
                    );
                  })}
                </React.Fragment>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function TaskCard({
  task,
  founders,
  dragging,
  onDragStart,
  onDragEnd,
  onClick,
  onAssigneeChange,
}: {
  task: Task;
  founders: Person[];
  dragging: boolean;
  onDragStart: (e: React.DragEvent, taskId: string) => void;
  onDragEnd: () => void;
  onClick: (task: Task) => void;
  onAssigneeChange: (taskId: string, personId: string) => void;
}) {
  const assigneeIds =
    task.assignee_ids && task.assignee_ids.length > 0
      ? task.assignee_ids
      : task.assignee
      ? [task.assignee]
      : [];
  const overdue = task.due_date ? new Date(task.due_date) < new Date() : false;
  const priority = task.priority ?? 'MEDIUM';
  const currentAssigneeId = assigneeIds[0] ?? '';
  const assigneeLabel = assigneeIds.length === 0
    ? 'Unassigned'
    : founders.filter((f) => assigneeIds.includes(f.id)).map((f) => f.name).join(', ');

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, task.id)}
      onDragEnd={onDragEnd}
      role="button"
      tabIndex={0}
      onClick={() => onClick(task)}
      onKeyDown={(e) => e.key === 'Enter' && onClick(task)}
      className="task-card"
      style={{
        padding: '10px 12px',
        background: 'white',
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        cursor: dragging ? 'grabbing' : 'grab',
        opacity: dragging ? 0.6 : 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        transition: 'box-shadow 150ms, border-color 150ms',
      }}
    >
      <div
        style={{
          fontWeight: 600,
          fontSize: '13px',
          lineHeight: 1.3,
          color: 'var(--color-text-primary)',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {task.title}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px',
          fontSize: '11px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
          <span
            aria-label={`Priority ${priority}`}
            title={priority}
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: PRIORITY_COLOR[priority],
              flexShrink: 0,
            }}
          />
          {task.due_date && (
            <span
              style={{
                color: overdue ? 'var(--color-coral)' : 'var(--color-text-muted)',
                whiteSpace: 'nowrap',
                fontWeight: overdue ? 600 : 400,
              }}
            >
              {formatDateDDMMYYYY(task.due_date)}
            </span>
          )}
        </div>
        <div style={{ position: 'relative', height: '28px', flexShrink: 0 }} title={assigneeLabel}>
          <AvatarStack people={founders} personIds={assigneeIds.length > 0 ? assigneeIds : [currentAssigneeId].filter(Boolean)} size={28} />
          <span
            className="task-card-chevron"
            aria-hidden
            style={{
              position: 'absolute',
              bottom: '-3px',
              right: '-3px',
              fontSize: '9px',
              color: 'var(--color-text-secondary)',
              background: 'white',
              borderRadius: '50%',
              width: '14px',
              height: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
              pointerEvents: 'none',
            }}
          >
            ▾
          </span>
          <select
            value={currentAssigneeId}
            onChange={(e) => onAssigneeChange(task.id, e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            draggable={false}
            aria-label="Change assignee"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              opacity: 0,
              cursor: 'pointer',
              border: 'none',
              padding: 0,
              background: 'transparent',
              appearance: 'none',
            }}
          >
            <option value="">Unassigned</option>
            {founders.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

interface ListViewProps {
  tasks: Task[];
  founders: Person[];
  sort: { key: SortKey; dir: SortDir };
  onSort: (key: SortKey) => void;
  onRowClick: (task: Task) => void;
  onAssigneeChange: (taskId: string, personIds: string[]) => void;
  selectedIds: Set<string>;
  onToggleOne: (id: string) => void;
  onToggleAll: () => void;
}

function ListView({ tasks, founders, sort, onSort, onRowClick, onAssigneeChange, selectedIds, onToggleOne, onToggleAll }: ListViewProps) {
  if (tasks.length === 0) {
    return <p style={{ color: 'var(--color-text-secondary)', padding: '24px 0' }}>No tasks.</p>;
  }
  const sortIndicator = (key: SortKey) =>
    sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '';

  const headerCellStyle: React.CSSProperties = {
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--color-text-secondary)',
    padding: '10px 12px',
    borderBottom: '1px solid var(--color-border)',
  };
  const sortableHeaderStyle: React.CSSProperties = { ...headerCellStyle, cursor: 'pointer', userSelect: 'none' };
  const cellStyle: React.CSSProperties = {
    padding: '12px',
    borderBottom: '1px solid var(--color-border)',
    fontSize: '14px',
    verticalAlign: 'middle',
  };

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: 'var(--radius)' }}>
      <thead>
        <tr>
          <th style={{ ...headerCellStyle, width: '40px' }}>
            <input
              type="checkbox"
              checked={tasks.length > 0 && tasks.every((t) => selectedIds.has(t.id))}
              ref={(el) => {
                if (!el) return;
                const sel = tasks.filter((t) => selectedIds.has(t.id)).length;
                el.indeterminate = sel > 0 && sel < tasks.length;
              }}
              onChange={onToggleAll}
            />
          </th>
          <th style={headerCellStyle}>Title</th>
          <th style={headerCellStyle}>Theme</th>
          <th style={sortableHeaderStyle} onClick={() => onSort('status')}>
            Status{sortIndicator('status')}
          </th>
          <th style={sortableHeaderStyle} onClick={() => onSort('priority')}>
            Priority{sortIndicator('priority')}
          </th>
          <th style={headerCellStyle}>Assignee</th>
          <th style={sortableHeaderStyle} onClick={() => onSort('due_date')}>
            Due{sortIndicator('due_date')}
          </th>
        </tr>
      </thead>
      <tbody>
        {tasks.map((task) => {
          const overdue = task.due_date ? new Date(task.due_date) < new Date() : false;
          return (
            <tr
              key={task.id}
              onClick={() => onRowClick(task)}
              style={{ cursor: 'pointer' }}
              onMouseOver={(e) => (e.currentTarget.style.background = 'var(--color-warm-bg)')}
              onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <td style={cellStyle} onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selectedIds.has(task.id)}
                  onChange={() => onToggleOne(task.id)}
                />
              </td>
              <td style={{ ...cellStyle, fontWeight: 500 }}>{task.title}</td>
              <td style={{ ...cellStyle, color: 'var(--color-text-secondary)', fontSize: '13px' }}>
                {projectLabel(task) || '—'}
              </td>
              <td style={cellStyle}>
                <span
                  style={{
                    fontSize: '12px',
                    padding: '4px 8px',
                    borderRadius: 'var(--radius)',
                    background: STATUS_BADGE[task.status].bg,
                    color: STATUS_BADGE[task.status].color,
                    fontWeight: 500,
                  }}
                >
                  {STATUS_LABEL[task.status]}
                </span>
              </td>
              <td style={{ ...cellStyle, color: PRIORITY_COLOR[task.priority ?? 'MEDIUM'], fontWeight: 600, fontSize: '13px' }}>
                {task.priority ?? 'MEDIUM'}
              </td>
              <td style={cellStyle} onClick={(e) => e.stopPropagation()}>
                <TaskAssigneesPicker
                  task={task}
                  people={founders}
                  onChange={(personIds) => onAssigneeChange(task.id, personIds)}
                />
              </td>
              <td style={{ ...cellStyle, fontSize: '13px', color: overdue ? 'var(--color-coral)' : 'var(--color-text-muted)' }}>
                {task.due_date ? formatDateDDMMYYYY(task.due_date) : '—'}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
